import React, { useState, useEffect } from "react";
import "./modal.css"; // Ensure you have some custom modal CSS
import { Button, DialogActions } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logUserAction from "utils/logging";

const SpecificModal = ({ selectedRow, closeModal, handleOutsideClick }) => {
  const [formData, setFormData] = useState({ ...selectedRow });
  const [isSpecificModalOpen, setIsSpecificModalOpen] = useState(false);
  const [circles, setCircles] = useState([]);
  const [originalData, setOriginalData] = useState({ ...selectedRow });
  const [changedFields, setChangedFields] = useState({}); // Track changes

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    // Ensure the name exists in the formData state
    if (Object.prototype.hasOwnProperty.call(formData, name)) {
      setFormData((prevData) => {
        const updatedData = { ...prevData, [name]: newValue };

        // Track changes if the field value has changed
        if (updatedData[name] !== originalData[name]) {
          setChangedFields((prevChanges) => ({
            ...prevChanges,
            [name]: updatedData[name], // Record the changed value
          }));
        }

        return updatedData;
      });
    } else {
      console.warn(`Field "${name}" is not defined in the formData state.`);
    }
  };

  // Fetch Circle options when the component mounts
  useEffect(() => {
    const fetchCircles = async () => {
      try {
        const response = await fetch(
          "https://api.bharatnet.sunshineiotronics.com/api/v2/circles"
        );
        const data = await response.json();
        setCircles(data); // Assuming the API returns an array of circles
      } catch (error) {
        toast.error("Failed to fetch circles");
      }
    };

    fetchCircles();
  }, []);

  const validateAndSubmit = async () => {
    // Validate numerical fields
    const numericFields = ["hb_freq", "rf_freq"];
    for (const field of numericFields) {
      if (isNaN(formData[field])) {
        alert(`Invalid number for ${field.replace("_", " ")}`);
        return;
      }
    }

    // Remove only the `_id` field and parse numeric fields
    const sanitizedData = Object.keys(formData).reduce((acc, key) => {
      if (key !== "_id") {
        // Exclude only the `_id` field
        acc[key] = numericFields.includes(key)
          ? parseInt(formData[key], 10)
          : formData[key];
      }
      return acc;
    }, {});

    // Ensure `site_id` is always included in the payload
    sanitizedData.site_id = formData.site_id;
    console.log(sanitizedData);

    try {
      const response = await fetch(
        "https://api.bharatnet.sunshineiotronics.com/api/v2/update_profile_by_site",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(sanitizedData),
        }
      );

      if (response.ok) {
        toast.success("Settings updated successfully!");
        setTimeout(() => {
          setIsSpecificModalOpen(false);
          window.location.reload();
        }, 1000);

        if (Object.keys(changedFields).length > 0) {
          const formattedChanges = Object.entries(changedFields)
            .map(([key, value]) => `${key}: ${value}`)
            .join(", ");
          logUserAction(`Changed Fields: ${formattedChanges}`);
        }
        setChangedFields({});
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.message}`);
        toast.warning("Failed to update settings. Please try again.");
      }
    } catch (error) {
      toast.error(`Network Error: ${error.message}`);
      toast.info(
        "Failed to update settings. Please check your network connection."
      );
    }
  };

  return (
    <div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="specific-modal" onClick={handleOutsideClick}>
        <div className="modal-content-1">
          <span className="close" onClick={closeModal}>
            &times;
          </span>
          <h2
            style={{
              display: "flex",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            Site Configuration Details
          </h2>
          <form
            id="settingsForm"
            className="modal-form"
            onSubmit={validateAndSubmit}
          >
            <div className="modal-form">
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Site ID</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.site_id}
                    onChange={handleChange}
                    name="site_id"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">MAC ID</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.mac_id}
                    name="mac_id"
                    disabled
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Heartbit (In Sec)</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.hb_freq}
                    onChange={handleChange}
                    name="hb_freq"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Periodic Data (In Sec)</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.rf_freq}
                    onChange={handleChange}
                    name="rf_freq"
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Serial No.</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.serial_no}
                    name="serial_no"
                    disabled
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Circle</label>
                  <select
                    className="input"
                    value={formData.state || ""}
                    name="state"
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Select a Circle
                    </option>
                    {circles.map((circle, index) => (
                      <option key={index} value={circle.state}>
                        {circle.state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">District</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.dist}
                    onChange={handleChange}
                    name="dist"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Taluka</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.tal}
                    name="tal"
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Village</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.village}
                    onChange={handleChange}
                    name="village"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Pin Code</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.pin_code}
                    onChange={handleChange}
                    name="pin_code"
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Latitude</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.lat}
                    onChange={handleChange}
                    name="lat"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Longitude</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.long}
                    onChange={handleChange}
                    name="long"
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Level 01 Contact</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.level_01_contact}
                    onChange={handleChange}
                    name="level_01_contact"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Level 02 Contact</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.level_02_contact}
                    onChange={handleChange}
                    name="level_02_contact"
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Email 01</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.email_1}
                    onChange={handleChange}
                    name="email_1"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Email 02</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.email_2}
                    onChange={handleChange}
                    name="email_2"
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Server State NOC</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.server_state_noc}
                    onChange={handleChange}
                    name="server_state_noc"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Server Centre NOC</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.server_centre_noc}
                    onChange={handleChange}
                    name="server_centre_noc"
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <label className="label">Software Version</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.sw_version}
                    disabled
                    name="sw_version"
                  />
                </div>
                <div className="modal-column">
                  <label className="label">Hardware Version</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.hw_version}
                    disabled
                    name="hw_version"
                  />
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-column">
                  <div className="toggle-switch-container">
                    <label className="label">Auto Download</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="auto_download"
                        checked={formData.auto_download === 1}
                        onChange={handleChange}
                      />
                      <div className="toggle-switch-background">
                        <div className="toggle-switch-handle"></div>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="modal-column">
                  <div className="toggle-switch-container">
                    <label className="label">Auto Upgrade</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="auto_upgrade"
                        checked={formData.auto_upgrade === 1}
                        onChange={handleChange}
                      />
                      <div className="toggle-switch-background">
                        <div className="toggle-switch-handle"></div>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="modal-column">
                  <div className="toggle-switch-container">
                    <label className="label">Remote Reboot</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="remote_reboot"
                        checked={formData.remote_reboot === 1}
                        onChange={handleChange}
                      />
                      <div className="toggle-switch-background">
                        <div className="toggle-switch-handle"></div>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="modal-column">
                  <div className="toggle-switch-container">
                    <label className="label">Factory Reset</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="factory_restore"
                        checked={formData.factory_restore === 1}
                        onChange={handleChange}
                      />
                      <div className="toggle-switch-background">
                        <div className="toggle-switch-handle"></div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <DialogActions className="specific-button">
                <Button onClick={closeModal} color="error">
                  Cancel
                </Button>
                <Button color="success" onClick={validateAndSubmit}>
                  Save Changes
                </Button>
              </DialogActions>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SpecificModal;
